import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Droplets, Plus, Camera, Calendar, ChevronDown, ChevronUp, Trash2, Edit2, User, X, Send, FileText, Check, MapPin, Phone, Mail, ImageIcon, Navigation, WifiOff, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// label = canonical French (stored in DB and used in client-facing reports);
// ru/md/uk = display-only translations
const VISIT_TYPES = [
  { value: 'hivernage', label: 'Mise en hivernage', ru: 'Консервация на зиму', md: 'Pregătire pentru iarnă', uk: 'Консервація на зиму' },
  { value: 'sortie_hivernage', label: 'Sortie d\'hivernage', ru: 'Расконсервация', md: 'Scoatere din iernare', uk: 'Розконсервація' },
  { value: 'entretien_simple', label: 'Entretien simple', ru: 'Простое обслуживание', md: 'Întreținere simplă', uk: 'Просте обслуговування' },
  { value: 'entretien_travaux', label: 'Entretien + travaux', ru: 'Обслуживание + работы', md: 'Întreținere + lucrări', uk: 'Обслуговування + роботи' },
  { value: 'sav', label: 'SAV', ru: 'Сервис (SAV)', md: 'Service (SAV)', uk: 'Сервіс (SAV)' },
];

const PRODUCTS = [
  { id: 'ph_moins', label: 'pH moins 20L', ru: 'pH минус 20 л', md: 'pH minus 20L', uk: 'pH мінус 20 л' },
  { id: 'chlore_liquide', label: 'Chlore liquide anti-tartre', ru: 'Жидкий хлор антинакипь', md: 'Clor lichid anticalcar', uk: 'Рідкий хлор проти накипу' },
  { id: 'sonde_ph', label: 'Changement de sonde pH', ru: 'Замена датчика pH', md: 'Înlocuirea sondei pH', uk: 'Заміна датчика pH' },
  { id: 'sonde_chlore', label: 'Changement sonde chlore', ru: 'Замена датчика хлора', md: 'Înlocuirea sondei de clor', uk: 'Заміна датчика хлору' },
  { id: 'appareil_ph', label: 'Appareil de traitement pH', ru: 'Прибор регулировки pH', md: 'Aparat de tratare pH', uk: 'Прилад регулювання pH' },
  { id: 'appareil_chlore', label: 'Appareil de traitement chlore', ru: 'Прибор обработки хлором', md: 'Aparat de tratare cu clor', uk: 'Прилад обробки хлором' },
  { id: 'disjoncteur', label: 'Disjoncteur', ru: 'Автоматический выключатель', md: 'Disjunctor', uk: 'Автоматичний вимикач' },
  { id: 'vis', label: 'Vis', ru: 'Винты', md: 'Șuruburi', uk: 'Гвинти' },
  { id: 'lattes_bois', label: 'Lattes de bois', ru: 'Деревянные рейки', md: 'Lamele din lemn', uk: 'Дерев\'яні рейки' },
  { id: 'peinture', label: 'Retouches peintures', ru: 'Подкраска', md: 'Retușuri de vopsea', uk: 'Підфарбування' },
  { id: 'autres', label: 'Autres', ru: 'Другое', md: 'Altele', uk: 'Інше' },
];

// Signature pad component
function SignaturePad({ label, value, onChange, clearLabel = 'Effacer' }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  useEffect(() => {
    if (value && canvasRef.current) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.drawImage(img, 0, 0, canvasRef.current.width, canvasRef.current.height);
      };
      img.src = value;
    }
  }, []);

  const resizeCanvas = () => {
    if (!containerRef.current || !canvasRef.current) return;
    const w = containerRef.current.offsetWidth;
    const canvas = canvasRef.current;
    canvas.width = w;
    canvas.height = Math.round(w * 0.35);
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (touch.clientX - rect.left) * scaleX,
      y: (touch.clientY - rect.top) * scaleY
    };
  };

  const endDraw = (e) => {
    if (e) e.preventDefault();
    if (!drawing) return;
    setDrawing(false);
    const ctx = canvasRef.current.getContext('2d');
    ctx.closePath();
    onChange(canvasRef.current.toDataURL('image/png'));
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const startDraw = (e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange('');
  };

  return (
    <div ref={containerRef}>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs font-semibold">{label}</Label>
        <button type="button" onClick={clear} className="text-[10px] text-red-500 hover:underline">{clearLabel}</button>
      </div>
      <canvas
        ref={canvasRef}
        className="w-full border-2 border-dashed border-gray-300 rounded-lg bg-white touch-none cursor-crosshair"
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
      />
    </div>
  );
}

// Navigation vers le client (Pavel 16/09/2026) : Waze et Google Maps en mode itineraire.
const wazeUrl = (addr) => `https://waze.com/ul?q=${encodeURIComponent(addr)}&navigate=yes`;
const mapsDirUrl = (addr) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;

const VISIT_TYPE_FR = { hivernage: 'mise en hivernage', sortie_hivernage: "sortie d'hivernage", entretien_simple: 'entretien', entretien_travaux: 'entretien et travaux', sav: 'intervention SAV' };

// Texte pre-rempli du mail qui accompagne le rapport PDF (toujours en francais : il part chez le client).
function defaultReportMessage(visit) {
  if (!visit) return '';
  const d = visit.date ? new Date(visit.date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const kind = VISIT_TYPE_FR[visit.visit_type] || 'intervention';
  const tech = visit.technician_name || visit.employee_name || '';
  return `Bonjour ${visit.client_name || ''},\n\nVeuillez trouver ci-joint le rapport de notre ${kind} du ${d} sur votre piscine${tech ? `, réalisée par ${tech}` : ''}.\n\nVous y retrouverez le détail des opérations effectuées, les produits utilisés et nos recommandations pour la suite.\n\nNous restons à votre disposition pour toute question.\n\nBien cordialement,\n\nL'équipe La Piscine Container\n09 73 89 48 39`;
}

// Heure au format HH:MM (Pavel 16/09/2026) : la saisie "1200" devient "12:00".
function formatHM(v) {
  const digits = String(v || '').replace(/[^0-9]/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return digits.slice(0, digits.length - 2) + ':' + digits.slice(digits.length - 2);
}

export default function MaintenancePage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const txt = (fr, ru, md, uk) => ({ fr, ru, md, uk }[language] || fr);
  const locale = { fr: 'fr-FR', ru: 'ru-RU', md: 'ro-RO', uk: 'uk-UA' }[language] || 'fr-FR';
  const [visits, setVisits] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editVisit, setEditVisit] = useState(null);
  const [expandedVisit, setExpandedVisit] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [showEmailDialog, setShowEmailDialog] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [photosProcessing, setPhotosProcessing] = useState(0);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [navAddr, setNavAddr] = useState(null);
  const photoCamBeforeRef = useRef(null);
  const photoLibBeforeRef = useRef(null);
  const photoCamAfterRef = useRef(null);
  const photoLibAfterRef = useRef(null);

  const TASK_LIST = [
    { key: 'nettoyage', label: txt('Nettoyage de la piscine', 'Чистка бассейна', 'Curățarea piscinei', 'Чищення басейну') },
    { key: 'analyse_eau', label: txt("Analyse de l'eau", 'Анализ воды', 'Analiza apei', 'Аналіз води') },
    { key: 'equipements', label: txt('Vérification du fonctionnement des équipements', 'Проверка работы оборудования', 'Verificarea funcționării echipamentelor', 'Перевірка роботи обладнання') },
    { key: 'filtre', label: txt('Nettoyage du filtre', 'Чистка фильтра', 'Curățarea filtrului', 'Чищення фільтра') },
    { key: 'produits', label: txt('Livraison produit Chlore liquide ou sel et pH moins', 'Доставка продуктов: жидкий хлор или соль и pH минус', 'Livrare produse: clor lichid sau sare și pH minus', 'Доставка продуктів: рідкий хлор або сіль і pH мінус') },
    { key: 'regulation', label: txt("Régulation du taux pH, chlore, d'alcalinité", 'Регулировка уровня pH, хлора, щёлочности', 'Reglarea nivelului de pH, clor, alcalinitate', 'Регулювання рівня pH, хлору, лужності') },
    { key: 'etalonnage', label: txt('Étalonnage des sondes des appareils traitement', 'Калибровка датчиков приборов обработки', 'Calibrarea sondelor aparatelor de tratare', 'Калібрування датчиків приладів обробки') },
    { key: 'peinture', label: txt('Peinture si rouille', 'Покраска при ржавчине', 'Vopsire dacă există rugină', 'Фарбування за наявності іржі') },
    { key: 'conforme', label: txt('Travaux réalisés conformément au devis ?', 'Работы выполнены согласно смете?', 'Lucrări realizate conform devizului?', 'Роботи виконані згідно з кошторисом?') },
  ];

  const [form, setForm] = useState({
    project_id: '', client_name: '', date: new Date().toISOString().split('T')[0],
    visit_type: 'entretien_simple', selected_products: [], other_product: '',
    report: '', next_actions: '', pre_diagnostic: '',
    planned_hours: '', actual_hours: '', technician_id: '', technician_name: '',
    tasks_done: {},
    photos_before: [], photos_after: [],
    signature_client: '', signature_technician: '',
    client_present: null
  });

  const isManager = user?.role === 'manager';

  useEffect(() => { reload(); }, []);

  // Reseau instable : on reessaie quelques fois avant d'abandonner (delai croissant).
  const getWithRetry = async (url, tries = 3) => {
    for (let i = 0; i < tries; i++) {
      try { return await axios.get(url); }
      catch (e) {
        if (i === tries - 1) throw e;
        await new Promise(r => setTimeout(r, 900 * (i + 1)));
      }
    }
  };

  const reload = () => {
    setLoading(true);
    setLoadError(false);
    fetchVisits();
    fetchProjects();
    fetchEmployees();
  };

  const fetchVisits = async () => {
    try {
      const res = await getWithRetry(`${API}/maintenance-visits`);
      setVisits(res.data || []);
      setLoadError(false);
    } catch {
      setLoadError(true); // on conserve les visites deja chargees s'il y en avait
    } finally { setLoading(false); }
  };

  const fetchProjects = async () => {
    try {
      const res = await getWithRetry(`${API}/projects/light`);
      const lpc = (res.data || []).filter(p => p.company === 'La Piscine Container' && !p.is_stock);
      setAllProjects(lpc);
      setClients(lpc.filter(p => p.status === 'maintenance' || p.status === 'entretien'));
    } catch {}
  };

  const fetchEmployees = async () => {
    try {
      const res = await getWithRetry(`${API}/users`);
      const lpcEmployees = (res.data || []).filter(u => u.company === 'La Piscine Container' || u.role === 'manager');
      setEmployees(lpcEmployees);
    } catch {}
  };

  const compressPhoto = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read'));
    reader.onloadend = () => {
      const img = new window.Image();
      img.onerror = () => reject(new Error('decode'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxW = 1000;
        const scale = Math.min(1, maxW / img.width);
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.55));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const handlePhotoUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;
    setPhotosProcessing(n => n + files.length);
    for (const file of files) {
      try {
        const compressed = await compressPhoto(file);
        setForm(prev => ({ ...prev, [type]: [...prev[type], compressed] }));
      } catch {
        toast.error(txt('Une photo n’a pas pu être ajoutée', 'Не удалось добавить фото', 'O fotografie nu a putut fi adăugată', 'Не вдалося додати фото'));
      } finally {
        setPhotosProcessing(n => Math.max(0, n - 1));
      }
    }
  };

  const toggleProduct = (id) => {
    setForm(prev => ({
      ...prev,
      selected_products: prev.selected_products.includes(id)
        ? prev.selected_products.filter(p => p !== id)
        : [...prev.selected_products, id]
    }));
  };

  const handleSave = async () => {
    if (!form.project_id || !form.date) {
      toast.error(txt('Client et date obligatoires', 'Клиент и дата обязательны', 'Clientul și data sunt obligatorii', "Клієнт і дата обов'язкові"));
      return;
    }
    if (photosProcessing > 0) {
      toast.error(txt('Photos en cours d’ajout, patientez une seconde', 'Фото добавляются, подождите', 'Fotografii în curs de adăugare, așteptați', 'Фото додаються, зачекайте'));
      return;
    }
    const payload = {
      ...form,
      products: form.selected_products.map(id => {
        const p = PRODUCTS.find(x => x.id === id);
        return id === 'autres' ? `Autres: ${form.other_product}` : p?.label;
      }).filter(Boolean).join(', ')
    };
    try {
      if (editVisit) {
        await axios.put(`${API}/maintenance-visits/${editVisit.id}`, payload);
        toast.success(txt('Visite modifiée', 'Визит изменён', 'Vizită modificată', 'Візит змінено'));
      } else {
        await axios.post(`${API}/maintenance-visits`, payload);
        toast.success(txt('Visite enregistrée', 'Визит сохранён', 'Vizită înregistrată', 'Візит збережено'));
      }
      setShowDialog(false);
      setEditVisit(null);
      resetForm();
      fetchVisits();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : txt('Erreur lors de la sauvegarde', 'Ошибка при сохранении', 'Eroare la salvare', 'Помилка збереження');
      toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    setConfirmDelete(id);
  };
  
  const confirmDeleteVisit = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/maintenance-visits/${confirmDelete}`);
      toast.success(txt('Supprimée', 'Удалено', 'Șters', 'Видалено'));
      setConfirmDelete(null);
      fetchVisits();
    } catch (error) { toast.error(error.response?.data?.detail || txt('Erreur', 'Ошибка', 'Eroare', 'Помилка')); }
  };

  const openEdit = (visit) => {
    setEditVisit(visit);
    const productList = (visit.products || '').split(', ').filter(Boolean);
    const selected = PRODUCTS.filter(p => productList.some(pl => pl.includes(p.label) || (p.id === 'autres' && pl.startsWith('Autres')))).map(p => p.id);
    const otherText = productList.find(p => p.startsWith('Autres:'))?.replace('Autres: ', '') || '';
    setForm({
      project_id: visit.project_id, client_name: visit.client_name, date: visit.date,
      visit_type: visit.visit_type, selected_products: selected, other_product: otherText,
      report: visit.report || '', next_actions: visit.next_actions || '',
      pre_diagnostic: visit.pre_diagnostic || '',
      planned_hours: visit.planned_hours || '', actual_hours: visit.actual_hours || '',
      technician_id: visit.technician_id || visit.employee_id || '', technician_name: visit.technician_name || visit.employee_name || '',
      tasks_done: visit.tasks_done || {},
      photos_before: visit.photos_before || [], photos_after: visit.photos_after || [],
      signature_client: visit.signature_client || '', signature_technician: visit.signature_technician || '',
      client_present: visit.client_present !== undefined ? visit.client_present : null
    });
    setShowDialog(true);
  };

  const openNew = () => { setEditVisit(null); resetForm(); setShowDialog(true); };

  const resetForm = () => {
    setForm({ project_id: '', client_name: '', date: new Date().toISOString().split('T')[0], visit_type: 'entretien_simple', selected_products: [], other_product: '', report: '', next_actions: '', pre_diagnostic: '', planned_hours: '', actual_hours: '', technician_id: user?.id || '', technician_name: user?.name || '', tasks_done: {}, photos_before: [], photos_after: [], signature_client: '', signature_technician: '', client_present: null });
  };

  const handleProjectSelect = (projectId) => {
    const project = allProjects.find(p => p.id === projectId);
    setForm(prev => ({ ...prev, project_id: projectId, client_name: project?.name || '' }));
  };

  const handleSendEmail = async (visit) => {
    if (!emailTo) { toast.error(txt('Email obligatoire', 'Email обязателен', 'Email obligatoriu', "Email обов'язковий")); return; }
    setSendingEmail(visit.id);
    try {
      await axios.post(`${API}/maintenance-visits/${visit.id}/send-report`, { email: emailTo, message: emailMessage });
      toast.success(txt('Rapport envoyé par email', 'Отчёт отправлен по email', 'Raport trimis prin email', 'Звіт надіслано на email'));
      setShowEmailDialog(null);
      setEmailTo('');
      setEmailMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || txt("Erreur d'envoi", 'Ошибка отправки', 'Eroare la trimitere', 'Помилка надсилання'));
    } finally { setSendingEmail(null); }
  };

  const handleExportPDF = async (visit) => {
    try {
      toast.info(txt('Génération du PDF...', 'Создание PDF...', 'Generare PDF...', 'Створення PDF...'));
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/maintenance-visits/${visit.id}/pdf-pages?token=${token}`);
      if (!res.ok) throw new Error('PDF error');
      const data = await res.json();
      setPdfPreview({ pages: data.pages || [], name: data.name || `rapport-${visit.client_name}-${visit.date}.pdf`, visitId: visit.id });
    } catch (err) {
      console.error('PDF error:', err);
      // Dernier recours : nouvel onglet (garde ses propres contrôles, ne bloque pas l'app)
      const token = localStorage.getItem('token');
      window.open(`${API}/maintenance-visits/${visit.id}/pdf?token=${token}`, '_blank');
    }
  };

  const closePdfPreview = () => setPdfPreview(null);

  const downloadPdfPreview = async () => {
    if (!pdfPreview) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/maintenance-visits/${pdfPreview.visitId}/pdf?token=${token}`);
      if (!res.ok) throw new Error('PDF error');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfPreview.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 3000);
    } catch {
      window.open(`${API}/maintenance-visits/${pdfPreview.visitId}/pdf?token=${token}`, '_blank');
    }
  };

  const getTypeLabel = (type) => {
    const v = VISIT_TYPES.find(v => v.value === type);
    return v ? txt(v.label, v.ru, v.md, v.uk) : type;
  };
  const getTypeColor = (type) => ({
    hivernage: 'bg-blue-100 text-blue-700', sortie_hivernage: 'bg-teal-100 text-teal-700',
    entretien_simple: 'bg-cyan-100 text-cyan-700', entretien_travaux: 'bg-amber-100 text-amber-700',
    sav: 'bg-orange-100 text-orange-700'
  }[type] || 'bg-gray-100 text-gray-700');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  if (loadError && visits.length === 0) return (
    <div className="flex flex-col items-center justify-center h-64 gap-4 text-center px-6" data-testid="maintenance-load-error">
      <WifiOff className="w-12 h-12 text-gray-300" />
      <div>
        <p className="font-medium text-gray-700">{txt('Connexion instable', 'Нестабильное соединение', 'Conexiune instabilă', 'Нестабільне зʼєднання')}</p>
        <p className="text-sm text-gray-500">{txt("Les visites d'entretien n'ont pas pu être chargées.", 'Не удалось загрузить визиты обслуживания.', 'Vizitele de întreținere nu au putut fi încărcate.', 'Не вдалося завантажити візити.')}</p>
      </div>
      <Button onClick={reload} className="btn-primary flex items-center gap-2" data-testid="maintenance-retry-btn">
        <RefreshCw className="w-4 h-4" /> {txt('Réessayer', 'Повторить', 'Reîncearcă', 'Повторити')}
      </Button>
    </div>
  );

  return (
    <div className="space-y-4 animate-fadeIn pb-24" data-testid="maintenance-page">
      {loadError && (
        <div className="card-base p-2 flex items-center justify-between gap-2 bg-orange-50 border border-orange-200 text-sm text-orange-700">
          <span className="flex items-center gap-2"><WifiOff className="w-4 h-4" /> {txt('Données peut-être incomplètes (connexion instable)', 'Данные могут быть неполными', 'Date posibil incomplete', 'Дані можуть бути неповними')}</span>
          <button onClick={reload} className="underline flex items-center gap-1 shrink-0"><RefreshCw className="w-3.5 h-3.5" /> {txt('Actualiser', 'Обновить', 'Reîmprospătează', 'Оновити')}</button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--color-primary)' }}>
          <Droplets className="w-6 h-6" />
          {txt('Entretien Piscines', 'Обслуживание бассейнов', 'Întreținere piscine', 'Обслуговування басейнів')}
        </h1>
        <Button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="new-visit-btn">
          <Plus className="w-4 h-4" /> {txt('Nouvelle visite', 'Новый визит', 'Vizită nouă', 'Новий візит')}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="card-base p-3 text-center">
          <span className="text-2xl font-bold text-cyan-600">{visits.length}</span>
          <p className="text-xs text-gray-500">{txt('Total visites', 'Всего визитов', 'Total vizite', 'Усього візитів')}</p>
        </div>
        <div className="card-base p-3 text-center">
          <span className="text-2xl font-bold text-emerald-600">{clients.length}</span>
          <p className="text-xs text-gray-500">{txt('Contrats', 'Контракты', 'Contracte', 'Контракти')}</p>
        </div>
        <div className="card-base p-3 text-center">
          <span className="text-2xl font-bold text-blue-600">{visits.filter(v => v.date?.startsWith(new Date().toISOString().slice(0, 7))).length}</span>
          <p className="text-xs text-gray-500">{txt('Ce mois', 'В этом месяце', 'Luna aceasta', 'Цього місяця')}</p>
        </div>
      </div>

      {visits.length === 0 ? (
        <div className="card-base p-8 text-center">
          <Droplets className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">{txt("Aucune visite d'entretien", 'Нет визитов обслуживания', 'Nicio vizită de întreținere', 'Немає візитів обслуговування')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visits.map(visit => {
            const isExpanded = expandedVisit === visit.id;
            return (
              <div key={visit.id} className="card-base overflow-hidden">
                <div className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setExpandedVisit(isExpanded ? null : visit.id)}>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const proj = allProjects.find(p => p.id === visit.project_id);
                      const photo = proj?.pool_photo || proj?.cover_photo;
                      return photo ? (
                        <img src={photo} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0 border" />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
                          <Droplets className="w-5 h-5 text-cyan-600" />
                        </div>
                      );
                    })()}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{txt('Votre rapport', 'Ваш отчёт', 'Raportul dvs.', 'Ваш звіт')}</p>
                      <h3 className="font-semibold text-sm text-slate-900 truncate">{visit.client_name}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-500">{new Date(visit.date + 'T12:00:00').toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${getTypeColor(visit.visit_type)}`}>{getTypeLabel(visit.visit_type)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {(visit.photos_before?.length > 0 || visit.photos_after?.length > 0) && <Camera className="w-4 h-4 text-gray-400" />}
                      {(visit.signature_client || visit.signature_technician) && <FileText className="w-4 h-4 text-green-400" />}
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t bg-gray-50 p-3 sm:p-4 space-y-3">
                    {/* Client info with clickable links */}
                    {(() => {
                      const proj = allProjects.find(p => p.id === visit.project_id);
                      const photo = proj?.pool_photo || proj?.cover_photo;
                      return proj ? (
                        <div className="space-y-2">
                          {photo && (
                            <img src={photo} alt="Piscine" className="w-full h-32 object-cover rounded-lg border cursor-pointer" onClick={() => setPreviewPhoto(photo)} />
                          )}
                          {proj.client_address && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); setNavAddr(proj.client_address); }}
                              className="w-full flex items-start gap-1.5 text-xs text-blue-700 p-2 bg-blue-50 rounded-lg hover:bg-blue-100 text-left">
                              <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /><span>{proj.client_address}</span>
                            </button>
                          )}
                          <div className="grid grid-cols-2 gap-2">
                            {proj.client_phone && (
                              <a href={`tel:${proj.client_phone}`} className="flex items-center justify-center gap-1.5 text-xs font-semibold text-green-700 py-2.5 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100">
                                <Phone className="w-4 h-4" /> {proj.client_phone}
                              </a>
                            )}
                            {proj.client_email && (
                              <a href={`mailto:${proj.client_email}`} className="flex items-center justify-center gap-1.5 text-xs font-semibold text-purple-700 py-2.5 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 min-w-0">
                                <Mail className="w-4 h-4 flex-shrink-0" /> <span className="truncate">{proj.client_email}</span>
                              </a>
                            )}
                          </div>
                        </div>
                      ) : null;
                    })()}
                    {/* Client contact info */}
                    {(() => {
                      const proj = allProjects.find(p => p.id === visit.project_id);
                      const phone = proj?.client_phone;
                      const email = proj?.client_email;
                      return (phone || email) ? (
                        <div className="bg-white rounded-lg p-2 border space-y-1 text-xs">
                          <p className="font-semibold text-gray-600">{txt('Informations client', 'Данные клиента', 'Informații client', 'Дані клієнта')}</p>
                          {phone && <a href={`tel:${phone}`} className="flex items-center gap-1.5 text-green-700 hover:underline"><Phone className="w-3 h-3" />{phone}</a>}
                          {email && <button type="button" onClick={(e) => { e.stopPropagation(); setShowEmailDialog(visit); setEmailTo(email); setEmailMessage(defaultReportMessage(visit)); }} className="flex items-center gap-1.5 text-purple-700 hover:underline"><Mail className="w-3 h-3" />{email}</button>}
                        </div>
                      ) : null;
                    })()}
                    {/* Client present */}
                    {visit.client_present !== undefined && visit.client_present !== null && (
                      <div className={`rounded-lg p-2 border text-xs font-medium flex items-center gap-1.5 ${visit.client_present ? 'bg-green-50 text-green-700 border-green-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`}>
                        <User className="w-3.5 h-3.5" />
                        {visit.client_present
                          ? txt("Client présent lors de l'intervention", 'Клиент присутствовал при работах', 'Client prezent la intervenție', 'Клієнт був присутній під час робіт')
                          : txt("Client absent lors de l'intervention", 'Клиент отсутствовал при работах', 'Client absent la intervenție', 'Клієнт був відсутній під час робіт')}
                      </div>
                    )}
                    {/* Key info */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-white rounded-lg p-2 border">
                        <span className="text-gray-500">{txt('Date', 'Дата', 'Data', 'Дата')}</span>
                        <p className="font-semibold text-slate-800">{new Date(visit.date + 'T12:00:00').toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 border">
                        <span className="text-gray-500">{txt('Technicien', 'Техник', 'Tehnician', 'Технік')}</span>
                        <p className="font-semibold text-slate-800">{visit.technician_name || visit.employee_name}</p>
                      </div>
                    </div>
                    {(visit.planned_hours || visit.actual_hours) && (
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {visit.planned_hours && <div className="bg-white rounded-lg p-2 border"><span className="text-gray-500">{txt('Arrivée', 'Прибытие', 'Sosire', 'Прибуття')}</span><p className="font-semibold text-slate-800">{formatHM(visit.planned_hours)}</p></div>}
                        {visit.actual_hours && <div className="bg-white rounded-lg p-2 border"><span className="text-gray-500">{txt('Départ', 'Отъезд', 'Plecare', "Від'їзд")}</span><p className="font-semibold text-slate-800">{formatHM(visit.actual_hours)}</p></div>}
                      </div>
                    )}
                    <div className={`rounded-lg p-2 border text-xs ${getTypeColor(visit.visit_type)}`}>
                      <span className="font-bold">{getTypeLabel(visit.visit_type)}</span>
                    </div>
                    {visit.products && <div><p className="text-xs font-semibold text-gray-600 mb-1">{txt('Produits utilisés', 'Использованные продукты', 'Produse utilizate', 'Використані продукти')}</p><div className="bg-white rounded-lg p-2 border space-y-1">{visit.products.split(', ').map((p, i) => <p key={i} className="text-sm text-gray-700 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />{p}</p>)}</div></div>}
                    {visit.report && <div><p className="text-xs font-semibold text-gray-600 mb-1">{txt('Ce qui a été fait', 'Что было сделано', 'Ce s-a făcut', 'Що було зроблено')}</p><p className="text-sm text-gray-700 bg-white rounded-lg p-2 border whitespace-pre-wrap">{visit.report}</p></div>}
                    {visit.next_actions && <div><p className="text-xs font-semibold text-blue-600 mb-1">{txt('A prévoir pour la prochaine fois', 'Запланировать на следующий раз', 'De prevăzut pentru data viitoare', 'Запланувати на наступний раз')}</p><p className="text-sm text-blue-700 bg-blue-50 rounded-lg p-2 border border-blue-200 whitespace-pre-wrap">{visit.next_actions}</p></div>}
                    {visit.photos_before?.length > 0 && <div><p className="text-xs font-semibold text-orange-600 mb-1">{txt('Photos AVANT', 'Фото ДО', 'Fotografii ÎNAINTE', 'Фото ДО')}</p><div className="flex gap-2 overflow-x-auto">{visit.photos_before.map((p, i) => <img key={i} src={p} alt="" className="w-20 h-20 rounded-lg object-cover border cursor-pointer" onClick={() => setPreviewPhoto(p)} />)}</div></div>}
                    {visit.photos_after?.length > 0 && <div><p className="text-xs font-semibold text-green-600 mb-1">{txt('Photos APRÈS', 'Фото ПОСЛЕ', 'Fotografii DUPĂ', 'Фото ПІСЛЯ')}</p><div className="flex gap-2 overflow-x-auto">{visit.photos_after.map((p, i) => <img key={i} src={p} alt="" className="w-20 h-20 rounded-lg object-cover border cursor-pointer" onClick={() => setPreviewPhoto(p)} />)}</div></div>}
                    {/* Signatures at the bottom */}
                    {(visit.signature_client || visit.signature_technician) && (
                      <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                        {visit.signature_client && <div><p className="text-xs text-gray-500 mb-1">{txt('Signature client', 'Подпись клиента', 'Semnătura clientului', 'Підпис клієнта')}</p><img src={visit.signature_client} alt="" className="h-14 border rounded bg-white p-1" /></div>}
                        {visit.signature_technician && <div><p className="text-xs text-gray-500 mb-1">{txt('Signature technicien', 'Подпись техника', 'Semnătura tehnicianului', 'Підпис техніка')}</p><img src={visit.signature_technician} alt="" className="h-14 border rounded bg-white p-1" /></div>}
                      </div>
                    )}
                    <div className="flex gap-2 pt-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => openEdit(visit)}><Edit2 className="w-3 h-3 mr-1" />{txt('Modifier', 'Изменить', 'Editează', 'Редагувати')}</Button>
                      <Button size="sm" variant="outline" className="text-xs text-cyan-600" onClick={() => handleExportPDF(visit)}><FileText className="w-3 h-3 mr-1" />PDF</Button>
                      <Button size="sm" variant="outline" className="text-xs text-green-600" onClick={() => { const proj = allProjects.find(p => p.id === visit.project_id); setShowEmailDialog(visit); setEmailTo(proj?.client_email || ''); setEmailMessage(defaultReportMessage(visit)); }}><Send className="w-3 h-3 mr-1" />{txt('Envoyer', 'Отправить', 'Trimite', 'Надіслати')}</Button>
                      <Button size="sm" variant="outline" className="text-xs text-red-600" onClick={() => handleDelete(visit.id)}><Trash2 className="w-3 h-3 mr-1" />{txt('Supprimer', 'Удалить', 'Șterge', 'Видалити')}</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Droplets className="w-5 h-5 text-cyan-600" />
              {editVisit
                ? txt('Modifier la visite', 'Изменить визит', 'Editează vizita', 'Редагувати візит')
                : txt("Nouvelle visite d'entretien", 'Новый визит обслуживания', 'Vizită nouă de întreținere', 'Новий візит обслуговування')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">{txt('Client / Chantier', 'Клиент / Объект', 'Client / Șantier', "Клієнт / Об'єкт")}</Label>
              <select value={form.project_id} onChange={(e) => handleProjectSelect(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="">{txt('— Sélectionner —', '— Выбрать —', '— Selectați —', '— Оберіть —')}</option>
                {clients.length > 0 && <optgroup label={txt('Contrats entretien', 'Контракты обслуживания', 'Contracte întreținere', 'Контракти обслуговування')}>{clients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>}
                <optgroup label={txt('Autres chantiers', 'Другие объекты', 'Alte șantiere', "Інші об'єкти")}>{allProjects.filter(p => !clients.find(c => c.id === p.id)).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>
              </select>
            </div>

            {/* Client info card - shows when a project is selected */}
            {form.project_id && (() => {
              const proj = allProjects.find(p => p.id === form.project_id);
              if (!proj) return null;
              const hasInfo = proj.client_address || proj.client_phone || proj.client_email || proj.pool_photo || proj.cover_photo;
              if (!hasInfo) return null;
              const poolImg = proj.pool_photo || proj.cover_photo;
              return (
                <div className="bg-slate-50 border rounded-xl p-3 space-y-2">
                  {poolImg && (
                    <img src={poolImg} alt="Piscine" className="w-full h-32 object-cover rounded-lg border cursor-pointer" onClick={() => setPreviewPhoto(poolImg)} />
                  )}
                  {proj.client_address && (
                    <button type="button" onClick={() => setNavAddr(proj.client_address)} className="w-full flex items-start gap-2 text-sm text-blue-700 p-2 bg-blue-50 rounded-lg hover:bg-blue-100 text-left">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{proj.client_address}</span>
                    </button>
                  )}
                  <div className="flex gap-2">
                    {proj.client_phone && (
                      <a href={`tel:${proj.client_phone}`} className="flex-1 flex items-center gap-2 text-sm text-green-700 p-2 bg-green-50 rounded-lg hover:bg-green-100">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>{proj.client_phone}</span>
                      </a>
                    )}
                    {proj.client_email && (
                      <a href={`mailto:${proj.client_email}`} className="flex-1 flex items-center gap-2 text-sm text-purple-700 p-2 bg-purple-50 rounded-lg hover:bg-purple-100">
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{proj.client_email}</span>
                      </a>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{txt('Date', 'Дата', 'Data', 'Дата')}</Label>
                <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">{txt("Type d'intervention", 'Тип работ', 'Tip intervenție', 'Тип робіт')}</Label>
                <select value={form.visit_type} onChange={e => setForm({...form, visit_type: e.target.value})} className="w-full mt-1 px-2 py-2 border border-gray-200 rounded-lg text-xs">
                  {VISIT_TYPES.map(t => <option key={t.value} value={t.value}>{txt(t.label, t.ru, t.md, t.uk)}</option>)}
                </select>
              </div>
            </div>

            {/* Technicien */}
            <div>
              <Label className="text-xs">{txt('Technicien', 'Техник', 'Tehnician', 'Технік')}</Label>
              <select value={form.technician_id} onChange={(ev) => {
                const emp = employees.find(u => u.id === ev.target.value);
                setForm({...form, technician_id: ev.target.value, technician_name: emp?.name || ''});
              }} className="w-full mt-1 px-2 py-2 border border-gray-200 rounded-lg text-xs">
                <option value="">{txt('— Sélectionner —', '— Выбрать —', '— Selectați —', '— Оберіть —')}</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
            </div>

            {/* Heures */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{txt('Arrivée', 'Прибытие', 'Sosire', 'Прибуття')}</Label>
                <Input type="text" inputMode="numeric" value={form.planned_hours} onChange={e => setForm({...form, planned_hours: formatHM(e.target.value)})} placeholder="08:00" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">{txt('Départ', 'Отъезд', 'Plecare', "Від'їзд")}</Label>
                <Input type="text" inputMode="numeric" value={form.actual_hours} onChange={e => setForm({...form, actual_hours: formatHM(e.target.value)})} placeholder="16:00" className="mt-1" />
              </div>
            </div>

            {/* Client présent */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <label className="text-xs font-medium text-gray-700">{txt('Client présent ?', 'Клиент присутствует?', 'Client prezent?', 'Клієнт присутній?')}</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({...form, client_present: true})}
                  className={`px-3 py-1 text-xs rounded-full font-semibold transition-all ${form.client_present === true ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                  {txt('Oui', 'Да', 'Da', 'Так')}
                </button>
                <button type="button" onClick={() => setForm({...form, client_present: false})}
                  className={`px-3 py-1 text-xs rounded-full font-semibold transition-all ${form.client_present === false ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                  {txt('Non', 'Нет', 'Nu', 'Ні')}
                </button>
              </div>
            </div>

            {/* Pré-diagnostic */}
            <div>
              <Label className="text-xs">{txt('Pré-diagnostic', 'Предварительный диагноз', 'Pre-diagnostic', 'Попередній діагноз')}</Label>
              <textarea value={form.pre_diagnostic} onChange={e => setForm({...form, pre_diagnostic: e.target.value})} placeholder={txt('État initial observé...', 'Наблюдаемое начальное состояние...', 'Starea inițială observată...', 'Початковий стан...')} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" rows={2} />
            </div>

            {/* Products - checkboxes */}
            <div>
              <Label className="text-xs">{txt('Produits utilisés', 'Использованные продукты', 'Produse utilizate', 'Використані продукти')}</Label>
              <div className="mt-1 space-y-2">
                {PRODUCTS.map(p => (
                  <button type="button" key={p.id} onClick={() => toggleProduct(p.id)} className={`flex items-center gap-3 w-full p-3 rounded-lg border text-left transition-all active:scale-[0.98] ${form.selected_products.includes(p.id) ? 'bg-cyan-50 border-cyan-400' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                    <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${form.selected_products.includes(p.id) ? 'bg-cyan-500' : 'border-2 border-gray-300'}`}>
                      {form.selected_products.includes(p.id) && <Check className="w-4 h-4 text-white" />}
                    </div>
                    <span className="text-sm font-medium">{txt(p.label, p.ru, p.md, p.uk)}</span>
                  </button>
                ))}
                {form.selected_products.includes('autres') && (
                  <Input value={form.other_product} onChange={e => setForm({...form, other_product: e.target.value})} placeholder={txt('Préciser le produit...', 'Уточните продукт...', 'Precizați produsul...', 'Уточніть продукт...')} className="ml-9" />
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs">{txt('Rapport / Ce qui a été fait', 'Отчёт / Что было сделано', 'Raport / Ce s-a făcut', 'Звіт / Що було зроблено')}</Label>
              <textarea value={form.report} onChange={e => setForm({...form, report: e.target.value})} placeholder={txt('État de la piscine, travaux effectués...', 'Состояние бассейна, выполненные работы...', 'Starea piscinei, lucrări efectuate...', 'Стан басейну, виконані роботи...')} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" rows={3} />
            </div>

            {/* Vérification des tâches - Oui/Non */}
            <div className="border-t pt-3">
              <Label className="text-xs font-bold text-slate-600 uppercase">{txt('Vérification des tâches', 'Проверка задач', 'Verificarea sarcinilor', 'Перевірка завдань')}</Label>
              <div className="mt-2 space-y-1">
                {TASK_LIST.map(task => (
                  <div key={task.key} className="flex items-center justify-between py-2 px-2 border-b border-gray-100">
                    <span className="text-xs text-gray-700 flex-1">{task.label}</span>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setForm(prev => ({...prev, tasks_done: {...prev.tasks_done, [task.key]: true}}))}
                        className={`px-3 py-1 text-xs rounded-full font-semibold transition-all ${form.tasks_done[task.key] === true ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
                        {txt('Oui', 'Да', 'Da', 'Так')}
                      </button>
                      <button type="button" onClick={() => setForm(prev => ({...prev, tasks_done: {...prev.tasks_done, [task.key]: false}}))}
                        className={`px-3 py-1 text-xs rounded-full font-semibold transition-all ${form.tasks_done[task.key] === false ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
                        {txt('Non', 'Нет', 'Nu', 'Ні')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs text-blue-600">{txt('A prévoir pour la prochaine fois', 'Запланировать на следующий раз', 'De prevăzut pentru data viitoare', 'Запланувати на наступний раз')}</Label>
              <textarea value={form.next_actions || ''} onChange={e => setForm({...form, next_actions: e.target.value})} placeholder={txt('Travaux à planifier, pièces à commander...', 'Работы к планированию, детали к заказу...', 'Lucrări de planificat, piese de comandat...', 'Роботи до планування, деталі до замовлення...')} className="w-full mt-1 px-3 py-2 border border-blue-200 rounded-lg text-sm resize-none bg-blue-50" rows={2} />
            </div>

            {/* Photos */}
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-orange-600 font-semibold">{txt('Photos AVANT', 'Фото ДО', 'Fotografii ÎNAINTE', 'Фото ДО')}</Label>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {form.photos_before.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={p} alt="" className="w-16 h-16 rounded-lg object-cover border" />
                      <button onClick={() => setForm(prev => ({...prev, photos_before: prev.photos_before.filter((_, idx) => idx !== i)}))} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={() => photoCamBeforeRef.current?.click()} className="flex-1 py-2 border-2 border-dashed border-orange-300 rounded-lg flex items-center justify-center gap-2 text-orange-500 hover:bg-orange-50 text-xs font-medium active:bg-orange-100">
                    <Camera className="w-4 h-4" /> {txt('Caméra', 'Камера', 'Cameră', 'Камера')}
                  </button>
                  <button type="button" onClick={() => photoLibBeforeRef.current?.click()} className="flex-1 py-2 border-2 border-dashed border-orange-300 rounded-lg flex items-center justify-center gap-2 text-orange-500 hover:bg-orange-50 text-xs font-medium active:bg-orange-100">
                    <ImageIcon className="w-4 h-4" /> {txt('Galerie', 'Галерея', 'Galerie', 'Галерея')}
                  </button>
                  <input ref={photoCamBeforeRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handlePhotoUpload(e, 'photos_before')} />
                  <input ref={photoLibBeforeRef} type="file" accept="image/*" multiple className="hidden" onChange={e => handlePhotoUpload(e, 'photos_before')} />
                </div>
              </div>
              <div>
                <Label className="text-xs text-green-600 font-semibold">{txt('Photos APRÈS', 'Фото ПОСЛЕ', 'Fotografii DUPĂ', 'Фото ПІСЛЯ')}</Label>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {form.photos_after.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={p} alt="" className="w-16 h-16 rounded-lg object-cover border" />
                      <button onClick={() => setForm(prev => ({...prev, photos_after: prev.photos_after.filter((_, idx) => idx !== i)}))} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={() => photoCamAfterRef.current?.click()} className="flex-1 py-2 border-2 border-dashed border-green-300 rounded-lg flex items-center justify-center gap-2 text-green-500 hover:bg-green-50 text-xs font-medium active:bg-green-100">
                    <Camera className="w-4 h-4" /> {txt('Caméra', 'Камера', 'Cameră', 'Камера')}
                  </button>
                  <button type="button" onClick={() => photoLibAfterRef.current?.click()} className="flex-1 py-2 border-2 border-dashed border-green-300 rounded-lg flex items-center justify-center gap-2 text-green-500 hover:bg-green-50 text-xs font-medium active:bg-green-100">
                    <ImageIcon className="w-4 h-4" /> {txt('Galerie', 'Галерея', 'Galerie', 'Галерея')}
                  </button>
                  <input ref={photoCamAfterRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handlePhotoUpload(e, 'photos_after')} />
                  <input ref={photoLibAfterRef} type="file" accept="image/*" multiple className="hidden" onChange={e => handlePhotoUpload(e, 'photos_after')} />
                </div>
              </div>
            </div>

            {/* Signatures */}
            <div className="border-t pt-3 space-y-3">
              <p className="text-xs font-bold text-slate-500 uppercase">{txt('Signatures', 'Подписи', 'Semnături', 'Підписи')}</p>
              <SignaturePad label={txt('Signature du client', 'Подпись клиента', 'Semnătura clientului', 'Підпис клієнта')} clearLabel={txt('Effacer', 'Очистить', 'Șterge', 'Очистити')} value={form.signature_client} onChange={v => setForm({...form, signature_client: v})} />
              <SignaturePad label={txt('Signature du technicien', 'Подпись техника', 'Semnătura tehnicianului', 'Підпис техніка')} clearLabel={txt('Effacer', 'Очистить', 'Șterge', 'Очистити')} value={form.signature_technician} onChange={v => setForm({...form, signature_technician: v})} />
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={() => { setShowDialog(false); setEditVisit(null); }} variant="outline" className="flex-1">{txt('Annuler', 'Отмена', 'Anulează', 'Скасувати')}</Button>
              <Button onClick={handleSave} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
                {editVisit ? txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти') : txt('Créer la visite', 'Создать визит', 'Creează vizita', 'Створити візит')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={!!showEmailDialog} onOpenChange={() => setShowEmailDialog(null)}>
        <DialogContent className="sm:max-w-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-green-600" />{txt('Envoyer le rapport', 'Отправить отчёт', 'Trimite raportul', 'Надіслати звіт')}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-gray-500">{txt('Client :', 'Клиент:', 'Client:', 'Клієнт:')} <strong>{showEmailDialog?.client_name}</strong></p>
            <div>
              <Label className="text-xs">{txt('Email du client', 'Email клиента', 'Emailul clientului', 'Email клієнта')}</Label>
              <Input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="client@email.com" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">{txt('Message au client', 'Сообщение клиенту', 'Mesaj către client', 'Повідомлення клієнту')}</Label>
              <textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)} rows={9}
                placeholder={txt('Bonjour, voici le compte rendu de notre passage...', 'Здравствуйте, вот отчёт...', 'Bună ziua, iată raportul...', 'Доброго дня, ось звіт...')}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" />
              <p className="flex items-center gap-1.5 text-xs text-gray-500 mt-1"><FileText className="w-3.5 h-3.5 text-cyan-600" />{txt('Le rapport PDF est joint automatiquement.', 'PDF-отчёт прикрепляется автоматически.', 'Raportul PDF este atașat automat.', 'PDF-звіт додається автоматично.')}</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => { setShowEmailDialog(null); setEmailMessage(''); }} variant="outline" className="flex-1">{txt('Annuler', 'Отмена', 'Anulează', 'Скасувати')}</Button>
              <Button onClick={() => handleSendEmail(showEmailDialog)} disabled={sendingEmail} className="flex-1 bg-green-600 hover:bg-green-700">
                {sendingEmail ? <div className="spinner w-4 h-4" /> : <><Send className="w-4 h-4 mr-2" />{txt('Envoyer', 'Отправить', 'Trimite', 'Надіслати')}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              {txt('Supprimer cette visite ?', 'Удалить этот визит?', 'Ștergeți această vizită?', 'Видалити цей візит?')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 py-2">{txt('Cette action est irréversible.', 'Это действие необратимо.', 'Această acțiune este ireversibilă.', 'Ця дія незворотна.')}</p>
          <div className="flex gap-3">
            <Button onClick={() => setConfirmDelete(null)} variant="outline" className="flex-1">{txt('Annuler', 'Отмена', 'Anulează', 'Скасувати')}</Button>
            <Button onClick={confirmDeleteVisit} className="flex-1 bg-red-600 hover:bg-red-700 text-white">{txt('Supprimer', 'Удалить', 'Șterge', 'Видалити')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Preview */}
      {previewPhoto && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[99999] p-4" onClick={() => setPreviewPhoto(null)}>
          <button onClick={() => setPreviewPhoto(null)} className="absolute top-4 right-4 text-white"><X className="w-8 h-8" /></button>
          <img src={previewPhoto} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}

      {/* Choix navigation (Pavel 16/09/2026) : ouvert en appuyant sur l'adresse */}
      {navAddr && (
        <div className="fixed inset-0 z-[99999] bg-black/50 flex items-end sm:items-center sm:justify-center" onClick={() => setNavAddr(null)}>
          <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
            <div className="flex items-start gap-2 text-sm text-slate-700"><MapPin className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" /><span>{navAddr}</span></div>
            <a href={wazeUrl(navAddr)} target="_blank" rel="noopener noreferrer" onClick={() => setNavAddr(null)}
              className="flex items-center justify-center gap-2 text-sm font-semibold text-white py-3 rounded-xl bg-[#33CCFF] hover:bg-[#1fb8ec]"><Navigation className="w-5 h-5" /> Waze</a>
            <a href={mapsDirUrl(navAddr)} target="_blank" rel="noopener noreferrer" onClick={() => setNavAddr(null)}
              className="flex items-center justify-center gap-2 text-sm font-semibold text-white py-3 rounded-xl bg-blue-600 hover:bg-blue-700"><Navigation className="w-5 h-5" /> {txt('Itinéraire', 'Маршрут', 'Traseu', 'Маршрут')}</a>
            <button type="button" onClick={() => setNavAddr(null)} className="w-full text-sm text-gray-500 py-2">{txt('Annuler', 'Отмена', 'Anulează', 'Скасувати')}</button>
          </div>
        </div>
      )}

      {/* PDF Preview */}
      {pdfPreview && (
        <div className="fixed inset-0 bg-black/90 flex flex-col z-[99999]">
          {/* Barre sous la zone sure (encoche / barre d'etat iOS) : sinon la croix est trop haute et inatteignable */}
          <div className="flex items-center justify-between px-4 py-3 bg-black/60" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
            <span className="text-white text-sm truncate mr-2">{pdfPreview.name}</span>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={downloadPdfPreview} className="text-white text-xs px-3 py-1.5 bg-white/15 rounded-lg hover:bg-white/25">
                {txt('Télécharger', 'Скачать', 'Descarcă', 'Завантажити')}
              </button>
              <button onClick={closePdfPreview} aria-label="Fermer" className="text-white p-2 -mr-2 rounded-full active:bg-white/20" style={{ minWidth: 44, minHeight: 44 }}><X className="w-7 h-7" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain p-2 space-y-2" style={{ WebkitOverflowScrolling: 'touch', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}>
            {pdfPreview.pages.map((src, i) => (
              <img key={i} src={src} alt={`Page ${i + 1}`} className="w-full rounded bg-white shadow" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
